import { useState, useCallback, useEffect } from 'react';
import { Message, ChatOption, ChatbotState } from './types';
import ChatbotService from './service';

export const useChatbot = () => {
  const INITIAL_OPTIONS: ChatOption[] = [
    { id: '1', label: '📦 Track Shipment', value: 'track shipment' },
    { id: '2', label: '💰 Get Quote', value: 'get shipping quote' },
    { id: '3', label: '🕒 Delivery Time', value: 'delivery time estimate' },
    { id: '4', label: '📍 Service Areas', value: 'service areas' },
    { id: '5', label: '📋 FAQs', value: 'frequently asked questions' },
    { id: '6', label: '📞 Contact Support', value: 'contact support' },
  ];

  const [state, setState] = useState<ChatbotState>({
    isOpen: false,
    messages: [],
    isLoading: false,
    sessionId: '',
  });
  const [options, setOptions] = useState<ChatOption[]>(INITIAL_OPTIONS);
  const chatbotService = ChatbotService.getInstance();

  useEffect(() => {
    if (state.isOpen && state.messages.length === 0) {
      const welcomeMessage: Message = {
        id: 'welcome',
        text: "Hello! 👋 I'm your logistics assistant. How can I help you today?",
        sender: 'bot',
        timestamp: new Date(),
        type: 'text',
      };
      setState(prev => ({ ...prev, messages: [welcomeMessage] }));
    }
  }, [state.isOpen]);

  const toggleChat = useCallback(() => {
    setState(prev => ({ ...prev, isOpen: !prev.isOpen }));
  }, []);

  const addMessage = useCallback((text: string, sender: 'user' | 'bot') => {
    const newMessage: Message = {
      id: `msg_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      text,
      sender,
      timestamp: new Date(),
    };
    setState(prev => ({ ...prev, messages: [...prev.messages, newMessage] }));
    return newMessage;
  }, []);

  const sendMessage = useCallback(
    async (text: string) => {
      addMessage(text, 'user');
      setOptions([]);
      setState(prev => ({ ...prev, isLoading: true }));

      try {
        const response = await chatbotService.sendMessage(text);

        if (response.success) {
          addMessage(response.data.response, 'bot');

          if (response.data.options) {
            setOptions(response.data.options);
          } else {
            setTimeout(() => setOptions(INITIAL_OPTIONS), 1000);
          }
        } else {
          addMessage('Sorry, I encountered an error. Please try again.', 'bot');
        }
      } catch (error) {
        console.error('Failed to send message:', error);
        addMessage('Network error. Please check your connection and try again.', 'bot');
      } finally {
        setState(prev => ({ ...prev, isLoading: false }));
      }
    },
    [addMessage, chatbotService],
  );

  const clearChat = useCallback(() => {
    setState(prev => ({ ...prev, messages: [], isLoading: false }));
    setOptions(INITIAL_OPTIONS);
    chatbotService.resetSession();
  }, [chatbotService]);

  return {
    ...state,
    options,
    toggleChat,
    sendMessage,
    clearChat,
  };
};
